/**
 * Section IDs for per-subsection attachment grouping.
 *
 * These IDs follow the pattern: {step-slug}.{subsection-slug}
 * They are used to:
 * - Group attachments by section in the database (request_section field)
 * - Display attachment counts per subsection
 * - Aggregate attachments in Supporting Documents
 */

export const SECTION_IDS = {
  // Step 1: Opportunity Information
  OPPORTUNITY_INFO: 'opportunity.info',
  OPPORTUNITY_REQUESTOR: 'opportunity.requestor',
  OPPORTUNITY_PROJECT_SUMMARY: 'opportunity.project-summary',
  OPPORTUNITY_PROJECT_DETAILS: 'opportunity.project-details',
  OPPORTUNITY_SCALE_TIMELINE: 'opportunity.scale-timeline',
  OPPORTUNITY_TEAM: 'opportunity.team',
  OPPORTUNITY_CUSTOMER: 'opportunity.customer',
  OPPORTUNITY_SITE: 'opportunity.site',
  OPPORTUNITY_BUILD_TYPE: 'opportunity.build-type',
  OPPORTUNITY_ADDITIONAL: 'opportunity.additional',

  // Step 2: Site Walk (assessment, address) + build type (BOM / RFI / config)
  SITE_WALK: 'site-walk.walk',
  SITE_WALK_BUILD_TYPE: 'site-walk.build-type',
  SITE_WALK_ADDITIONAL: 'site-walk.additional',

  // Step 2: Site information & dimensions
  SITE_DIMENSIONS_INFO: 'site-dimensions.info',
  SITE_DIMENSIONS_ADDITIONAL: 'site-dimensions.additional',

  // Step 3: Facility Power Readiness
  FACILITY_POWER: 'facility-power.facility-power',
  FACILITY_POWER_LARGE_UPS: 'facility-power.large-ups',
  FACILITY_POWER_DISTRIBUTION: 'facility-power.power-distribution',
  FACILITY_POWER_ADDITIONAL: 'facility-power.additional',
  FACILITY_POWER_LARGE_UPS_ADDITIONAL: 'facility-power.large-ups-additional',
  FACILITY_POWER_DISTRIBUTION_ADDITIONAL:
    'facility-power.power-distribution-additional',

  // Step 4: Cooling Readiness
  COOLING_READINESS_INFO: 'cooling-readiness.info',
  COOLING_READINESS_ADVANCED: 'cooling-readiness.advanced',
  COOLING_READINESS_ADDITIONAL: 'cooling-readiness.additional',

  // Step 5: Rack Configuration
  RACKS_DIMENSIONS: 'racks.dimensions',
  RACKS_ADDITIONAL: 'racks.additional',
  RACK_CONFIGURATIONS: 'racks.configurations', // Multi-rack feature (#995)

  // Step 6: Rack Power
  RACK_POWER_INFO: 'rack-power.info',
  RACK_POWER_ADDITIONAL: 'rack-power.additional',

  // Step 7: Customer IT Equipment
  CUSTOMER_IT_INFO: 'customer-it.info',
  CUSTOMER_IT_ADDITIONAL: 'customer-it.additional',

  // Step 8: Small UPS Requirements
  SMALL_UPS_REQUIREMENTS: 'small-ups.requirements',
  SMALL_UPS_ADDITIONAL: 'small-ups.additional',

  // Step 9: Liquid Cooling
  LIQUID_COOLING_PROJECT: 'liquid-cooling.project-requirements',
  LIQUID_COOLING_SYSTEM: 'liquid-cooling.system-level',
  LIQUID_COOLING_PRIMARY: 'liquid-cooling.primary-loop',
  LIQUID_COOLING_SECONDARY: 'liquid-cooling.secondary-loop',
  LIQUID_COOLING_FLUID_NETWORK: 'liquid-cooling.fluid-network',
  LIQUID_COOLING_NOTES: 'liquid-cooling.notes',

  // Step 10: Monitoring & Management
  MONITORING_INFO: 'monitoring.info',
  MONITORING_ADDITIONAL: 'monitoring.additional',

  // Step 11: Supporting Documents (main step for general uploads)
  SUPPORTING_DOCUMENTS: 'supporting-documents',

  // Step 12: Feedback
  FEEDBACK: 'feedback',
} as const;

export type SectionId = (typeof SECTION_IDS)[keyof typeof SECTION_IDS];

/**
 * Human-readable labels for each section ID.
 * Used in the Supporting Documents aggregation view.
 */
export const SECTION_LABELS: Record<SectionId, string> = {
  // Opportunity
  [SECTION_IDS.OPPORTUNITY_INFO]: 'Opportunity Information',
  [SECTION_IDS.OPPORTUNITY_REQUESTOR]: 'Requestor',
  [SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY]: 'Project Summary',
  [SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS]: 'Project Details',
  [SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE]: 'Feasibility',
  [SECTION_IDS.OPPORTUNITY_TEAM]: 'Team Information',
  [SECTION_IDS.OPPORTUNITY_CUSTOMER]: 'Customer Information',
  [SECTION_IDS.OPPORTUNITY_SITE]: 'Site Information',
  [SECTION_IDS.OPPORTUNITY_BUILD_TYPE]: 'Build Type',
  [SECTION_IDS.OPPORTUNITY_ADDITIONAL]: 'Opportunity - Additional',

  // Site Walk
  [SECTION_IDS.SITE_WALK]: 'Site Walk',
  [SECTION_IDS.SITE_WALK_BUILD_TYPE]: 'Build type',
  [SECTION_IDS.SITE_WALK_ADDITIONAL]: 'Site Walk - Additional',

  // Site information & dimensions
  [SECTION_IDS.SITE_DIMENSIONS_INFO]: 'Site Information',
  [SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL]: 'Site Dimensions',

  // Facility Power
  [SECTION_IDS.FACILITY_POWER]: 'Facility Power',
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS]: 'Large UPS',
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION]: 'Power Distribution',
  [SECTION_IDS.FACILITY_POWER_ADDITIONAL]: 'Facility Power - Additional',
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL]: 'Large UPS - Additional',
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL]:
    'Power Distribution - Additional',

  // Cooling Readiness
  // Labels mirror the on-screen card titles so Supporting Documents matches the step UI.
  // The active Cooling step renders the main card as "Cooling System" and tags its
  // attachments to COOLING_READINESS_ADVANCED (COOLING_READINESS_INFO is legacy).
  [SECTION_IDS.COOLING_READINESS_INFO]: 'Cooling Information',
  [SECTION_IDS.COOLING_READINESS_ADVANCED]: 'Cooling System',
  [SECTION_IDS.COOLING_READINESS_ADDITIONAL]: 'Additional Information',

  // Racks
  [SECTION_IDS.RACKS_DIMENSIONS]: 'Rack Dimensions',
  [SECTION_IDS.RACKS_ADDITIONAL]: 'Racks - Additional rack notes',
  [SECTION_IDS.RACK_CONFIGURATIONS]: 'Racks',

  // Rack Power
  [SECTION_IDS.RACK_POWER_INFO]: 'Rack Power Information',
  [SECTION_IDS.RACK_POWER_ADDITIONAL]: 'Rack Power - Additional',

  // Customer IT
  [SECTION_IDS.CUSTOMER_IT_INFO]: 'Customer IT Equipment Information',
  [SECTION_IDS.CUSTOMER_IT_ADDITIONAL]: 'Customer IT - Additional',

  // Small UPS
  [SECTION_IDS.SMALL_UPS_REQUIREMENTS]: 'Small UPS',
  [SECTION_IDS.SMALL_UPS_ADDITIONAL]: 'Small UPS - Additional',

  // Liquid Cooling
  [SECTION_IDS.LIQUID_COOLING_PROJECT]: 'Liquid Cooling',
  [SECTION_IDS.LIQUID_COOLING_SYSTEM]: 'Liquid Cooling - System Level',
  [SECTION_IDS.LIQUID_COOLING_PRIMARY]: 'Primary Loop / FWS',
  [SECTION_IDS.LIQUID_COOLING_SECONDARY]: 'Secondary Loop / TCS',
  [SECTION_IDS.LIQUID_COOLING_FLUID_NETWORK]: 'Secondary Fluid Network',
  [SECTION_IDS.LIQUID_COOLING_NOTES]: 'Additional Information',

  // Monitoring
  [SECTION_IDS.MONITORING_INFO]: 'Monitoring Management',
  [SECTION_IDS.MONITORING_ADDITIONAL]: 'Monitoring - Additional',

  // Supporting Documents
  [SECTION_IDS.SUPPORTING_DOCUMENTS]: 'Supporting Documents',

  // Feedback
  [SECTION_IDS.FEEDBACK]: 'Feedback',
};

/**
 * Mapping from sectionId to the JSON permission field names that belong to that subsection.
 * Used to check subsection-level write access for attachment permissions.
 *
 * Format: { sectionId: { section: 'Permission Section Name', fields: ['Field Name 1', 'Field Name 2'] } }
 */
export const SECTION_PERMISSION_FIELDS: Record<
  string,
  { section: string; fields: string[] }
> = {
  // Opportunity Information subsections
  [SECTION_IDS.OPPORTUNITY_INFO]: {
    section: 'Opportunity Information',
    fields: [
      'Requestor Email',
      'Requestor Role',
      'Requestor Name',
      'Requestor Phone',
    ],
  },
  [SECTION_IDS.OPPORTUNITY_REQUESTOR]: {
    section: 'Opportunity Information',
    fields: [
      'Requestor Email',
      'Requestor Role',
      'Requestor Name',
      'Requestor Phone',
    ],
  },
  [SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY]: {
    section: 'Opportunity Information',
    fields: ['Region', 'Customer Name', 'Description'],
  },
  [SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS]: {
    section: 'Opportunity Information',
    fields: ['Deal Registration Number', 'Opportunity Number', 'Description'],
  },
  [SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE]: {
    section: 'Opportunity Information',
    fields: ['Description'],
  },
  [SECTION_IDS.OPPORTUNITY_TEAM]: {
    section: 'Opportunity Information',
    fields: ['Vertiv Sponsors', 'Vertiv Contributors', 'Partner Contributors'],
  },
  [SECTION_IDS.OPPORTUNITY_CUSTOMER]: {
    section: 'Opportunity Information',
    fields: [
      'Region',
      'Customer Name',
      'Deal Registration Number',
      'Opportunity Number',
      'Description',
    ],
  },
  [SECTION_IDS.OPPORTUNITY_SITE]: {
    section: 'Opportunity Information',
    fields: ['Data Halls', 'Integrated Solution', 'Site Address'],
  },
  [SECTION_IDS.OPPORTUNITY_BUILD_TYPE]: {
    section: 'Opportunity Information',
    fields: ['Assessment Date', 'Retrofit or New Build', 'Total Racks'],
  },
  [SECTION_IDS.OPPORTUNITY_ADDITIONAL]: {
    section: 'Opportunity Information',
    fields: ['Additional Information'],
  },
  [SECTION_IDS.SITE_WALK]: {
    section: 'Opportunity Information',
    fields: [
      'Site Assessment Required',
      'Assessment Date',
      'Site Address',
      'Site Walk Notes',
    ],
  },
  [SECTION_IDS.SITE_WALK_BUILD_TYPE]: {
    section: 'Opportunity Information',
    fields: [
      'BOM Internal Routing',
      'Request for Information',
      'Config Team Instructions',
    ],
  },
  [SECTION_IDS.SITE_WALK_ADDITIONAL]: {
    section: 'Opportunity Information',
    fields: ['Site Walk Notes'],
  },

  // Site Dimensions subsections
  [SECTION_IDS.SITE_DIMENSIONS_INFO]: {
    section: 'Site Dimensions',
    fields: [
      'Ceiling Height',
      'Room Length',
      'Room Width',
      'Flooring Type',
      'Floor Weight Support',
      'Additional Information',
    ],
  },
  [SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL]: {
    section: 'Site Dimensions',
    fields: ['Additional Information'],
  },

  // Facility Power subsections
  [SECTION_IDS.FACILITY_POWER]: {
    section: 'Facility Power Readiness',
    fields: [
      'Main Voltage',
      'Total IT Load',
      'Available Power',
      'Final MW Size',
      'Description',
      'Main nominal Amps',
    ],
  },
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS]: {
    section: 'Facility Power Readiness',
    fields: [
      'Large UPS Installed',
      'Existing UPS Model',
      'Redundancy Level',
      'UPS Redundancy Required',
      'Battery Chemistry',
      'Battery Runtime',
      'Power Location',
      'Large UPS Description',
      'Fire Suppression Needs',
    ],
  },
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION]: {
    section: 'Facility Power Readiness',
    fields: [
      'Distribution Voltage',
      'Busway Amperage',
      'Busway Length',
      'Busway or Under Floor',
      'UPS Output Voltage',
      'Tap Off Box Requirements',
    ],
  },
  [SECTION_IDS.FACILITY_POWER_ADDITIONAL]: {
    section: 'Facility Power Readiness',
    fields: ['Additional Power Information'],
  },
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL]: {
    section: 'Facility Power Readiness',
    fields: ['Additional Power Information'],
  },
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL]: {
    section: 'Facility Power Readiness',
    fields: ['Additional Power Information'],
  },

  // Cooling Readiness subsections
  // Note: In CoolingReadinessStep.tsx, the "Cooling Readiness Information" section contains:
  // - Existing Cooling System, Chilled Water Available, Available Cooling Capacity
  [SECTION_IDS.COOLING_READINESS_INFO]: {
    section: 'Cooling Readiness',
    fields: [
      'Existing Cooling System',
      'Chilled Water Available',
      'Available Cooling Capacity',
    ],
  },
  // Note: COOLING_READINESS_ADVANCED backs the active "Cooling System" card in the
  // repeatable cooling editor (see CoolingSystemEditFormContent). These are the main
  // cooling fields users edit today. This list drives the section-level attachment
  // write gate (SubsectionForm.hasSectionWriteAccess), so it must contain only live
  // permission-backed labels.
  [SECTION_IDS.COOLING_READINESS_ADVANCED]: {
    section: 'Cooling Readiness',
    fields: ['Cooling Redundancy Required', 'Rack Temperature Rise'],
  },
  [SECTION_IDS.COOLING_READINESS_ADDITIONAL]: {
    section: 'Cooling Readiness',
    fields: ['Additional Information'],
  },

  // Racks subsections
  [SECTION_IDS.RACKS_DIMENSIONS]: {
    section: 'Racks',
    fields: [
      'Rack Length',
      'Rack Height',
      'Rack Width',
      'Network Racks',
      'Network Rack Types',
      'Rack Type',
      'Clear Space Above',
    ],
  },
  [SECTION_IDS.RACKS_ADDITIONAL]: {
    section: 'Racks',
    fields: ['Additional Rack Information', 'Additional Information'],
  },
  [SECTION_IDS.RACK_CONFIGURATIONS]: {
    section: 'Racks',
    fields: [
      'Rack Type',
      'Rack Platform',
      'Rack Description',
      'Rack Use',
      'Rack Quantity',
      'Rack Length',
      'Rack Height',
      'Rack Width',
      'Rack Color',
    ],
  },

  // Rack Power subsections
  [SECTION_IDS.RACK_POWER_INFO]: {
    section: 'Rack Power',
    fields: [
      'PDU Make/Model',
      'Mounting Type',
      'RPDUs per Rack',
      'Outlet Type',
      'Outlet Quantities',
      'AC or DC Power',
      'Max kW per Rack',
      'PDU Input',
      'PDU Output',
    ],
  },
  [SECTION_IDS.RACK_POWER_ADDITIONAL]: {
    section: 'Rack Power',
    fields: ['Additional Information'],
  },

  // Customer IT Equipment subsections
  [SECTION_IDS.CUSTOMER_IT_INFO]: {
    section: 'Customer IT Equipment',
    fields: [
      'GPU Models',
      'AI Servers',
      'DC or AC Power',
      'Power Shelf Included',
      'Cooling Type',
      'Infrastructure',
    ],
  },
  [SECTION_IDS.CUSTOMER_IT_ADDITIONAL]: {
    section: 'Customer IT Equipment',
    fields: ['Additional Information'],
  },

  // Small UPS (single section)
  [SECTION_IDS.SMALL_UPS_REQUIREMENTS]: {
    section: 'Small UPS',
    fields: [
      'UPS Make/Model',
      'Runtime',
      'UPS Rating',
      'UPS Type',
      'Input Phase',
      'Input Voltage',
      'Output Voltage',
      'Mount Type',
      'Maintenance Bypass Required',
      'Expected Load Growth',
      'Will Load Be On Generator?',
      'What Sensors Are Required',
    ],
  },
  [SECTION_IDS.SMALL_UPS_ADDITIONAL]: {
    section: 'Small UPS',
    fields: ['Additional Information'],
  },

  // Liquid Cooling subsections
  [SECTION_IDS.LIQUID_COOLING_PROJECT]: {
    section: 'Liquid Cooling',
    fields: ['Liquid Cooling Required'],
  },
  [SECTION_IDS.LIQUID_COOLING_SYSTEM]: {
    section: 'Liquid Cooling',
    fields: [
      '2D Datahall Layout',
      'Total IT Load',
      'Liquid Cooled Racks',
      'Air Cooled Racks',
      'Liquid Cooling Percentage',
      'Total IT Load per Datahall',
      'Redundancy Strategy',
    ],
  },
  [SECTION_IDS.LIQUID_COOLING_PRIMARY]: {
    section: 'Liquid Cooling',
    fields: [
      'Glycol Percentage',
      'Primary Supply Temp',
      'Primary Return Temp',
      'Primary Connection Type',
      'Primary Valve',
    ],
  },
  [SECTION_IDS.LIQUID_COOLING_SECONDARY]: {
    section: 'Liquid Cooling',
    fields: [
      'Coolant',
      'Secondary Supply Temp',
      'Secondary Return Temp',
      'Flowrate Required',
      'Filtration',
      'Max Pressure',
      'Rack Pressure Drop',
      'External Pressure Required',
      'Secondary Connection Type',
      'Secondary Network Required',
      'Additional Information SFN',
      'Additional Attachments',
    ],
  },
  [SECTION_IDS.LIQUID_COOLING_FLUID_NETWORK]: {
    section: 'Liquid Cooling',
    fields: [
      'Secondary Network Required',
      '3D Model Datahall',
      '2D Layout Datahall',
      'Ceiling Height',
      'Ceiling Support Rating',
      'Rack Arrangement',
      'Floor Type',
      'Raised Floor Depth',
      'CDU Arrangement',
      'CDU Redundancy',
      'CDU Primary Piping',
      'CDU Secondary Piping',
      'TCS Flow Pathway',
      'TCS Special Requirements',
      'TCS Arrangement',
      'TCS Location',
      'TCS Control Type',
      'TCS Connection Size',
      'TCS Connection Type',
      'TCS Max Pressure',
      'TCS Max Temperature',
      'TCS Pipe Diameter',
      'TCS Coolant',
      'TCS Flowrate per Rack',
      'Additional Information TCS',
    ],
  },
  [SECTION_IDS.LIQUID_COOLING_NOTES]: {
    section: 'Liquid Cooling',
    fields: ['Additional Information'],
  },

  // Monitoring Management subsections
  [SECTION_IDS.MONITORING_INFO]: {
    section: 'Monitoring Management',
    fields: ['Monitoring Make/Model', 'Monitoring Configuration'],
  },
  [SECTION_IDS.MONITORING_ADDITIONAL]: {
    section: 'Monitoring Management',
    fields: ['Additional Information'],
  },

  // Supporting Documents (single section)
  [SECTION_IDS.SUPPORTING_DOCUMENTS]: {
    section: 'Supporting Documentation',
    fields: ['Supporting Documents'],
  },

  // Feedback (single section)
  [SECTION_IDS.FEEDBACK]: {
    section: 'Feedback',
    fields: ['Rating', 'Comments'],
  },
};

/**
 * Stage definitions for grouping sections.
 * Each stage represents a major form step.
 *
 * Story 5.1 (Epic 5): LARGE_UPS and POWER_DISTRIBUTION are first-class stages,
 * mirroring the step-registry split from #1330. Attachments carrying
 * `facility-power.large-ups*` / `facility-power.power-distribution*` section ids
 * roll up to these new stages, so the PDF appendix and the client grouped
 * Supporting Documents view render them under their own bucket instead of the
 * combined Facility Power Readiness bucket.
 */
export const STAGES = {
  OPPORTUNITY: 'opportunity',
  SITE_DIMENSIONS: 'site-dimensions',
  FACILITY_POWER: 'facility-power',
  LARGE_UPS: 'large-ups',
  POWER_DISTRIBUTION: 'power-distribution',
  COOLING_READINESS: 'cooling-readiness',
  RACKS: 'racks',
  RACK_POWER: 'rack-power',
  CUSTOMER_IT: 'customer-it',
  SMALL_UPS: 'small-ups',
  LIQUID_COOLING: 'liquid-cooling',
  MONITORING: 'monitoring',
  SUPPORTING_DOCUMENTS: 'supporting-documents',
  FEEDBACK: 'feedback',
} as const;

export type Stage = (typeof STAGES)[keyof typeof STAGES];

/**
 * Human-readable labels for each stage.
 */
// Wording mirrors the left-nav step catalog (requestFormStepRegistry) so Summary and
// grouped-attachment section headers match the navigation. Leading numbers are intentional
// and stripped for display via stripStageNumber().
export const STAGE_LABELS: Record<Stage, string> = {
  [STAGES.OPPORTUNITY]: '1. Opportunity',
  [STAGES.SITE_DIMENSIONS]: '2. Site Information',
  [STAGES.FACILITY_POWER]: '3. Facility Power Readiness',
  [STAGES.LARGE_UPS]: '4. Large UPS',
  [STAGES.POWER_DISTRIBUTION]: '5. Power Distribution',
  [STAGES.COOLING_READINESS]: '6. Cooling',
  [STAGES.RACKS]: '7. Racks',
  [STAGES.RACK_POWER]: '8. Rack Power',
  [STAGES.CUSTOMER_IT]: '9. Customer IT Equipment',
  [STAGES.SMALL_UPS]: '10. Small UPS',
  [STAGES.LIQUID_COOLING]: '11. Liquid Cooling',
  [STAGES.MONITORING]: '12. Monitoring & Management',
  [STAGES.SUPPORTING_DOCUMENTS]: '13. Supporting Documents',
  [STAGES.FEEDBACK]: '14. Feedback',
};

/**
 * Strip leading number prefix from stage labels (e.g. "1. Opportunity" → "Opportunity").
 * STAGE_LABELS intentionally include numbers; consumers that display grouped
 * attachments without numbering should use this helper.
 */
export const stripStageNumber = (label: string): string =>
  label.replace(/^\d+\.\s*/, '');

/**
 * Map of section IDs to their parent stage.
 */
export const SECTION_TO_STAGE: Record<SectionId, Stage> = {
  // Opportunity
  [SECTION_IDS.OPPORTUNITY_INFO]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_REQUESTOR]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_PROJECT_SUMMARY]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_PROJECT_DETAILS]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_SCALE_TIMELINE]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_TEAM]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_CUSTOMER]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_SITE]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_BUILD_TYPE]: STAGES.OPPORTUNITY,
  [SECTION_IDS.OPPORTUNITY_ADDITIONAL]: STAGES.OPPORTUNITY,

  // Site Walk
  [SECTION_IDS.SITE_WALK]: STAGES.SITE_DIMENSIONS,
  [SECTION_IDS.SITE_WALK_BUILD_TYPE]: STAGES.SITE_DIMENSIONS,
  [SECTION_IDS.SITE_WALK_ADDITIONAL]: STAGES.SITE_DIMENSIONS,

  // Site information & dimensions
  [SECTION_IDS.SITE_DIMENSIONS_INFO]: STAGES.SITE_DIMENSIONS,
  [SECTION_IDS.SITE_DIMENSIONS_ADDITIONAL]: STAGES.SITE_DIMENSIONS,

  // Facility Power — Story 5.1: Large UPS + Power Distribution sections roll up
  // to their own stages so their attachments render under their own PDF appendix
  // buckets and Supporting Documents groups. Only `.facility-power` and
  // `.additional` stay on STAGES.FACILITY_POWER.
  [SECTION_IDS.FACILITY_POWER]: STAGES.FACILITY_POWER,
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS]: STAGES.LARGE_UPS,
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION]: STAGES.POWER_DISTRIBUTION,
  [SECTION_IDS.FACILITY_POWER_ADDITIONAL]: STAGES.FACILITY_POWER,
  [SECTION_IDS.FACILITY_POWER_LARGE_UPS_ADDITIONAL]: STAGES.LARGE_UPS,
  [SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL]:
    STAGES.POWER_DISTRIBUTION,

  // Cooling Readiness
  [SECTION_IDS.COOLING_READINESS_INFO]: STAGES.COOLING_READINESS,
  [SECTION_IDS.COOLING_READINESS_ADVANCED]: STAGES.COOLING_READINESS,
  [SECTION_IDS.COOLING_READINESS_ADDITIONAL]: STAGES.COOLING_READINESS,

  // Racks
  [SECTION_IDS.RACKS_DIMENSIONS]: STAGES.RACKS,
  [SECTION_IDS.RACKS_ADDITIONAL]: STAGES.RACKS,
  [SECTION_IDS.RACK_CONFIGURATIONS]: STAGES.RACKS,

  // Rack Power — #997 QA-D: Rack Power is a subsection of the Racks step, so its
  // attachments group UNDER Racks (not a separate top-level bucket) across the
  // client grouped view, Summary, and PDF appendix. Mirror this in
  // PDF_SECTION_TO_STAGE (server/utils/pdfFieldMapper.ts) so pdfFieldMapper-sync
  // stays green. STAGES.RACK_POWER remains defined (orphan label) but nothing
  // resolves to it as a group anymore.
  [SECTION_IDS.RACK_POWER_INFO]: STAGES.RACKS,
  [SECTION_IDS.RACK_POWER_ADDITIONAL]: STAGES.RACKS,

  // Customer IT
  [SECTION_IDS.CUSTOMER_IT_INFO]: STAGES.CUSTOMER_IT,
  [SECTION_IDS.CUSTOMER_IT_ADDITIONAL]: STAGES.CUSTOMER_IT,

  // Small UPS
  [SECTION_IDS.SMALL_UPS_REQUIREMENTS]: STAGES.SMALL_UPS,
  [SECTION_IDS.SMALL_UPS_ADDITIONAL]: STAGES.SMALL_UPS,

  // Liquid Cooling
  [SECTION_IDS.LIQUID_COOLING_PROJECT]: STAGES.LIQUID_COOLING,
  [SECTION_IDS.LIQUID_COOLING_SYSTEM]: STAGES.LIQUID_COOLING,
  [SECTION_IDS.LIQUID_COOLING_PRIMARY]: STAGES.LIQUID_COOLING,
  [SECTION_IDS.LIQUID_COOLING_SECONDARY]: STAGES.LIQUID_COOLING,
  [SECTION_IDS.LIQUID_COOLING_FLUID_NETWORK]: STAGES.LIQUID_COOLING,
  [SECTION_IDS.LIQUID_COOLING_NOTES]: STAGES.LIQUID_COOLING,

  // Monitoring
  [SECTION_IDS.MONITORING_INFO]: STAGES.MONITORING,
  [SECTION_IDS.MONITORING_ADDITIONAL]: STAGES.MONITORING,

  // Supporting Documents
  [SECTION_IDS.SUPPORTING_DOCUMENTS]: STAGES.SUPPORTING_DOCUMENTS,

  // Feedback
  [SECTION_IDS.FEEDBACK]: STAGES.FEEDBACK,
};

/**
 * Get the substage label (the portion after the stage prefix).
 * E.g., "opportunity.info" -> "Opportunity Information"
 */
export const getSubstageLabel = (sectionId: string): string => {
  return SECTION_LABELS[sectionId as SectionId] || sectionId;
};

/** Legacy snake_case section ids (pre dot-notation migration). */
const LEGACY_SECTION_LABELS: Record<string, string> = {
  opportunity_information: 'Opportunity Information',
  site_dimensions: 'Site Walk',
  facility_power_readiness: 'Facility Power Readiness',
  cooling_readiness: 'Cooling Information',
  rack_configuration: 'Rack Configuration',
  rack_power: 'Rack Power',
  customer_it_equipment: 'Customer IT Equipment',
  small_ups_requirements: 'Small UPS Requirements',
  liquid_cooling: 'Liquid Cooling',
  monitoring_management: 'Monitoring & Management',
  supporting_documents: 'Supporting Documents',
};

/** Legacy snake_case → parent stage (grouped view + Summary appendix). */
export const LEGACY_SECTION_TO_STAGE: Record<string, Stage> = {
  opportunity_information: STAGES.OPPORTUNITY,
  site_dimensions: STAGES.SITE_DIMENSIONS,
  facility_power_readiness: STAGES.FACILITY_POWER,
  cooling_readiness: STAGES.COOLING_READINESS,
  rack_configuration: STAGES.RACKS,
  rack_power: STAGES.RACKS, // #997 QA-D: legacy Rack Power attachments group under Racks
  customer_it_equipment: STAGES.CUSTOMER_IT,
  small_ups_requirements: STAGES.SMALL_UPS,
  liquid_cooling: STAGES.LIQUID_COOLING,
  monitoring_management: STAGES.MONITORING,
  supporting_documents: STAGES.SUPPORTING_DOCUMENTS,
};

/** Legacy snake_case → canonical dot-notation section id where known. */
const LEGACY_SECTION_TO_CANONICAL: Partial<Record<string, SectionId>> = {
  opportunity_information: SECTION_IDS.OPPORTUNITY_INFO,
  site_dimensions: SECTION_IDS.SITE_DIMENSIONS_INFO,
  facility_power_readiness: SECTION_IDS.FACILITY_POWER,
  cooling_readiness: SECTION_IDS.COOLING_READINESS_INFO,
  rack_configuration: SECTION_IDS.RACK_CONFIGURATIONS,
  rack_power: SECTION_IDS.RACK_POWER_INFO,
  customer_it_equipment: SECTION_IDS.CUSTOMER_IT_INFO,
  small_ups_requirements: SECTION_IDS.SMALL_UPS_REQUIREMENTS,
  liquid_cooling: SECTION_IDS.LIQUID_COOLING_PROJECT,
  monitoring_management: SECTION_IDS.MONITORING_INFO,
  supporting_documents: SECTION_IDS.SUPPORTING_DOCUMENTS,
};

function lookupLegacySectionKey(sectionId: string): string | undefined {
  if (sectionId in LEGACY_SECTION_TO_STAGE) {
    return sectionId;
  }
  const snakeKey = sectionId.toLowerCase().replace(/[-.\s]+/g, '_');
  if (snakeKey in LEGACY_SECTION_TO_STAGE) {
    return snakeKey;
  }
  return undefined;
}

function inferStageFromSectionPrefix(sectionId: string): Stage | null {
  const prefix = sectionId.split('.')[0]?.toLowerCase().replace(/_/g, '-');
  if (!prefix) {
    return null;
  }
  // #997 QA-D: Rack Power is merged under Racks. The two known rack-power section
  // ids resolve to RACKS via SECTION_TO_STAGE above, but this last-resort prefix
  // inference would otherwise still yield the orphan RACK_POWER stage for any
  // OTHER `rack-power.*` id (resurrecting a standalone "Rack Power" group). Keep
  // the merge complete: rack-power always infers RACKS, never RACK_POWER.
  if (prefix === STAGES.RACK_POWER) {
    return STAGES.RACKS;
  }
  const stageValues = Object.values(STAGES) as Stage[];
  return stageValues.includes(prefix as Stage) ? (prefix as Stage) : null;
}

function findCanonicalSectionId(sectionId: string): SectionId | null {
  if (sectionId in SECTION_TO_STAGE) {
    return sectionId as SectionId;
  }

  const lower = sectionId.toLowerCase();
  for (const key of Object.keys(SECTION_TO_STAGE)) {
    if (key.toLowerCase() === lower) {
      return key as SectionId;
    }
  }

  const legacyKey = lookupLegacySectionKey(sectionId);
  if (legacyKey && LEGACY_SECTION_TO_CANONICAL[legacyKey]) {
    return LEGACY_SECTION_TO_CANONICAL[legacyKey] ?? null;
  }

  return null;
}

/**
 * Strip a trailing `.unit.{configurationId}` suffix, yielding the base section
 * id. The client's single authored `.unit.` strip — used by
 * `resolveSectionIdForLookup` and `getStageFromSection` — mirroring the
 * server's `stripRepeatableUnitSuffix` (`server/utils/repeatableSectionId.ts`,
 * `lastIndexOf('.unit.')` + `> 0` guard). Locked against client/server drift
 * by the shared case table (`repeatableSectionId.cases.ts`) via
 * `repeatableSectionIdContract.test.ts` (NFR8).
 */
function stripRepeatableUnitSuffix(sectionId: string): string {
  const unitMarkerIndex = sectionId.lastIndexOf('.unit.');
  return unitMarkerIndex > 0 ? sectionId.slice(0, unitMarkerIndex) : sectionId;
}

/**
 * Resolve DB / upload section strings to the canonical registry id when possible.
 * Handles legacy snake_case and case-variant dot-notation (`Facility-Power.Facility-Power`).
 */
export function resolveSectionIdForLookup(sectionId: string): string {
  const normalizedSectionId = stripRepeatableUnitSuffix(sectionId);
  return findCanonicalSectionId(normalizedSectionId) ?? normalizedSectionId;
}

/**
 * Attachment section label for flat/grouped chrome. Always resolves to the
 * subsection label (Racks pattern) so every step reads consistently, e.g.
 * `cooling-readiness.info` → `Cooling Information`,
 * `facility-power.large-ups` → `Large UPS`,
 * `opportunity.project-summary` → `Project Summary`.
 * Never surfaces raw dotted ids (#997 Bugs 6/7/8); facility-power family ids are
 * treated as independent subsections, not a merged step label.
 */
export function formatAttachmentSectionPathDisplay(
  section?: string | null,
): string {
  if (!section) {
    return '';
  }
  const canonical = resolveSectionIdForLookup(section);

  const sectionLabel = lookupSectionLabel(canonical);
  if (sectionLabel) {
    return sectionLabel;
  }

  const rawSegments = canonical.split('.').filter(Boolean);
  if (rawSegments.length === 0) {
    return '';
  }

  const lastSegment = rawSegments[rawSegments.length - 1];
  return lookupSectionLabel(lastSegment) ?? humanizeSectionSegment(lastSegment);
}

function humanizeSectionSegment(segment: string): string {
  return segment
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function lookupSectionLabel(section: string): string | undefined {
  const direct = SECTION_LABELS[section as SectionId];
  if (direct) {
    return direct;
  }

  const legacy = LEGACY_SECTION_LABELS[section];
  if (legacy) {
    return legacy;
  }

  const normalized = section.toLowerCase();
  for (const [sectionId, label] of Object.entries(SECTION_LABELS)) {
    if (sectionId.toLowerCase() === normalized) {
      return label;
    }
  }

  return undefined;
}

/**
 * Get human-readable label for a section ID.
 * Supports dot-notation ids (`facility-power.large-ups`), legacy snake_case, and
 * falls back to the last path segment (`power-distribution` → Power Distribution).
 */
export const getSectionLabel = (section?: string | null): string => {
  if (!section) {
    return 'General';
  }

  const normalizedSection = resolveSectionIdForLookup(section);

  const known = lookupSectionLabel(normalizedSection);
  if (known) {
    return known;
  }

  if (normalizedSection.includes('.')) {
    const lastSegment = normalizedSection.split('.').pop();
    if (lastSegment) {
      const segmentLabel = lookupSectionLabel(lastSegment);
      if (segmentLabel) {
        return segmentLabel;
      }
      return humanizeSectionSegment(lastSegment);
    }
  }

  return humanizeSectionSegment(normalizedSection);
};

/**
 * Get stage from a section ID.
 * Supports canonical dot-notation, case variants, and legacy snake_case ids.
 *
 * Story 5.1: strip any `.unit.{configurationId}` suffix (shared
 * `stripRepeatableUnitSuffix` helper) before the lookup so per-unit repeatable
 * attachments resolve to the same stage as their base section id. Without this
 * strip, `facility-power.large-ups.unit.42` fell through the canonical-id
 * lookup and landed on the `facility-power` stage via prefix inference —
 * routing Large UPS unit attachments to the wrong bucket after the LU/PD
 * taxonomy split. Mirrors the server's `stripRepeatableUnitSuffix` used in
 * `renderRequestDocumentXml.ts:232`.
 */
export const getStageFromSection = (sectionId: string): Stage | null => {
  const strippedSectionId = stripRepeatableUnitSuffix(sectionId);

  const canonical = findCanonicalSectionId(strippedSectionId);
  if (canonical) {
    return SECTION_TO_STAGE[canonical];
  }

  const legacyKey = lookupLegacySectionKey(strippedSectionId);
  if (legacyKey) {
    return LEGACY_SECTION_TO_STAGE[legacyKey] ?? null;
  }

  return inferStageFromSectionPrefix(strippedSectionId);
};

/**
 * Get stage label.
 */
export const getStageLabel = (stage: Stage): string => {
  return STAGE_LABELS[stage] || stage;
};

/**
 * Short human-readable labels for each stage (without numbers).
 * Used for compact badge displays.
 */
export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  [STAGES.OPPORTUNITY]: 'Opportunity',
  [STAGES.SITE_DIMENSIONS]: 'Site Walk',
  [STAGES.FACILITY_POWER]: 'Facility Power',
  [STAGES.LARGE_UPS]: 'Large UPS',
  [STAGES.POWER_DISTRIBUTION]: 'Power Distribution',
  [STAGES.COOLING_READINESS]: 'Cooling',
  [STAGES.RACKS]: 'Racks',
  [STAGES.RACK_POWER]: 'Rack Power',
  [STAGES.CUSTOMER_IT]: 'Customer IT',
  [STAGES.SMALL_UPS]: 'Small UPS',
  [STAGES.LIQUID_COOLING]: 'Liquid Cooling',
  [STAGES.MONITORING]: 'Monitoring',
  [STAGES.SUPPORTING_DOCUMENTS]: 'Supporting Docs',
  [STAGES.FEEDBACK]: 'Feedback',
};

/**
 * Get short stage label (without number prefix).
 */
export const getShortStageLabel = (stage: Stage): string => {
  return STAGE_SHORT_LABELS[stage] || stage;
};

/**
 * Stage order for sorting grouped attachments.
 */
export const STAGE_ORDER: Stage[] = [
  STAGES.OPPORTUNITY,
  STAGES.SITE_DIMENSIONS,
  STAGES.FACILITY_POWER,
  // Story 5.1 — mirrors step-registry navOrder (facility-power 30 → large-ups 35
  // → power-distribution 36) so the PDF appendix subsections and the client
  // grouped attachment tiles match on-screen step order.
  STAGES.LARGE_UPS,
  STAGES.POWER_DISTRIBUTION,
  STAGES.COOLING_READINESS,
  STAGES.RACKS,
  // #997 QA-D: RACK_POWER removed — Rack Power attachments now group under RACKS
  // (SECTION_TO_STAGE maps rack-power.* → RACKS). The STAGES.RACK_POWER constant
  // and its STAGE_LABELS entry remain for the Stage union + PDF label-count sync.
  STAGES.CUSTOMER_IT,
  STAGES.SMALL_UPS,
  STAGES.LIQUID_COOLING,
  STAGES.MONITORING,
  STAGES.SUPPORTING_DOCUMENTS,
  STAGES.FEEDBACK,
];
